/**
 * This module is for displaying product popularity by views and/or purchases within a specifed timeframe
 *
 * @module views/components/SocialTags
 * @memberof -Common
 */
import './SocialTags.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import Icon from '@ulta/core/components/Icon/Icon';
import Markdown from '@ulta/core/components/Markdown/Markdown';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { hasItems } from '@ulta/core/utils/array/array';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

/**
 * Represents a SocialTags component
 *
 * @method
 * @param {SocialTagsProps} props - React properties passed from composition
 * @returns SocialTags
 */
export const SocialTags = function( props ){

  const {
    items,
    id,
    rootMargin,
    root,
    threshold } = props;
  const componentRef = useRef();
  useIntersectionObserver(
    componentRef,
    {
      root: root,
      rootMargin: rootMargin,
      threshold: threshold
    },
    handleIntersection( props )
  );

  if( !hasItems( items ) ){
    return null;
  }

  return (
    <div className='SocialTags'
      id={ id }
      ref={ componentRef }
    >
      <ul className='SocialTags__socialTagList'>
        { items.map( ( item, index ) => (
          <li key={ `${id}-${index}` }
            className='SocialTags__socialTag'
          >
            { item.icon &&
            <Icon
              size={ 'm' }
              name={ item.icon }
              className='SocialTags__socialTagIcon'
              aria-hidden={ true }
            /> }
            <Markdown
              className='SocialTags__socialTagMessage'
              bodyStyle='body-3'
              cmsMarkdownContent={ item.message }
            />
          </li>
        ) ) }
      </ul>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef SocialTagsProps
 * @type {object}
 * @property {array} items - Array of SocialTags
 * @property {string} id - component id for the SocialTags
 * @property {string} productId - product id to pass to the initAction
 */
export const propTypes =  {
  items: PropTypes.array,
  id: PropTypes.string,
  productId: PropTypes.string
};

SocialTags.propTypes = propTypes;

export default SocialTags;
